import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isStaleChunkLoadError } from '@/lib/lazyWithRetry';
import { removePrerenderShells } from '@/lib/staticHero';

type Props = { children: ReactNode };
type State = { error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Drop crawl/boot shells so the error UI is not stacked under "Page loading".
    removePrerenderShells();
    console.error('[ShopGND] Uncaught UI error', error, info.componentStack);
    if (typeof window !== 'undefined' && 'gtag' in window) {
      (window as Window & { gtag?: (...args: unknown[]) => void }).gtag?.('event', 'exception', {
        description: error.message,
        fatal: false,
      });
    }
  }

  render() {
    if (this.state.error) {
      const cartCorrupt =
        this.state.error.message.includes('lines.reduce') ||
        this.state.error.message.includes('lines.map');
      const staleChunk =
        isStaleChunkLoadError(this.state.error) ||
        /Failed to load application bundle/i.test(this.state.error.message);
      return (
        <div className="mx-auto flex min-h-[50vh] max-w-lg flex-col items-center justify-center px-4 py-16 text-center">
          <h1 className="text-xl font-bold text-slate-900 dark:text-white">Something went wrong</h1>
          <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
            {cartCorrupt
              ? 'Your saved cart data looks corrupted. Clear it and refresh to continue.'
              : staleChunk
                ? 'A new version of ShopGND was published while this tab was open. Refresh once to load the latest app.'
                : 'Try refreshing the page. If the problem continues, contact support.'}
          </p>
          {this.state.error ? (
            <pre className="mt-4 max-h-40 overflow-auto rounded-lg bg-slate-100 p-3 text-left text-xs text-slate-700 dark:bg-slate-800 dark:text-slate-300">
              {this.state.error.message}
            </pre>
          ) : null}
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            {cartCorrupt ? (
              <button
                type="button"
                className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800"
                onClick={() => {
                  localStorage.removeItem('shopgnd-cart');
                  window.location.reload();
                }}
              >
                Clear cart & refresh
              </button>
            ) : null}
            {staleChunk ? (
              <button
                type="button"
                className="rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800"
                onClick={() => {
                  try {
                    sessionStorage.removeItem('shopgnd-chunk-reload');
                  } catch {
                    /* private mode */
                  }
                  removePrerenderShells();
                  window.location.reload();
                }}
              >
                Refresh page
              </button>
            ) : (
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-800"
                onClick={() => this.setState({ error: null })}
              >
                Try again
              </button>
            )}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
